import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, ClipboardCheck, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/toast';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

const EvaluationFormModal = ({ isOpen, onClose, evaluation, onSuccess }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [confirmed, setConfirmed] = useState(false);

  // Staff feedback state
  const [staffFeedback, setStaffFeedback] = useState({});

  // Department feedback state
  const [departmentFeedback, setDepartmentFeedback] = useState({
    compliance_readiness: '',
    patient_care_safety: '',
    cost_effectiveness: ''
  });

  useEffect(() => {
    if (!isOpen) return;

    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (!evaluation) {
        toast.error('No evaluation data found.');
        onClose();
        return;
      }
      
      fetchTeamMembers(parsedUser.id);
    }
  }, [isOpen, evaluation]);

  const fetchTeamMembers = async (managerId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/manager/team-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allMembers = response.data.team_progress || [];
      const members = allMembers.filter(member => member.user_id !== managerId);
      setTeamMembers(members);
      
      const initialFeedback = {};
      members.forEach(member => {
        initialFeedback[member.employee_id] = {
          compliance: '',
          skill_gap: '',
          patient_care: ''
        };
      });
      setStaffFeedback(initialFeedback);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      if (error.response?.status === 403) {
        toast.error('You are not assigned as a Reporting Manager');
      } else {
        toast.error('Failed to load team members');
      }
      setLoading(false);
    }
  };

  const handleStaffFeedbackChange = (employeeId, category, value) => {
    setStaffFeedback(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [category]: value
      }
    }));
  };

  const handleDepartmentFeedbackChange = (category, value) => {
    setDepartmentFeedback(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const validateForm = () => {
    for (const employeeId in staffFeedback) {
      const feedback = staffFeedback[employeeId];
      if (!feedback.compliance || !feedback.skill_gap || !feedback.patient_care) {
        toast.error('Please rate all staff members across all categories');
        return false;
      }
    }

    if (!departmentFeedback.compliance_readiness || 
        !departmentFeedback.patient_care_safety || 
        !departmentFeedback.cost_effectiveness) {
      toast.error('Please complete all department-level questions');
      return false;
    }

    if (!confirmed) {
      toast.error('Please confirm the accuracy of your feedback');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        manager_id: user.id,
        manager_name: user.full_name || user.name,
        staff_feedback: Object.keys(staffFeedback).map(employeeId => ({
          employee_id: employeeId,
          employee_name: teamMembers.find(m => m.employee_id === employeeId)?.employee_name,
          ratings: staffFeedback[employeeId]
        })),
        department_feedback: departmentFeedback,
        submitted_at: new Date().toISOString()
      };

      await axios.post(`${API}/manager/evaluation-form`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Evaluation form submitted successfully!');
      
      // Reset form
      const initialFeedback = {};
      teamMembers.forEach(member => {
        initialFeedback[member.employee_id] = {
          compliance: '',
          skill_gap: '',
          patient_care: ''
        };
      });
      setStaffFeedback(initialFeedback);
      setDepartmentFeedback({
        compliance_readiness: '',
        patient_care_safety: '',
        cost_effectiveness: ''
      });
      setConfirmed(false);
      
      // Call success callback and close modal
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error('Failed to submit evaluation form');
    } finally {
      setSubmitting(false);
    }
  };

  const isCompleted = evaluation?.status === 'completed';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-[#0056B3]" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isCompleted ? 'View Evaluation Details' : 'Staff and Department Feedback'}
              </h2>
              <p className="text-sm text-gray-600">
                {evaluation?.template_name || 'Manager Evaluation Form'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {evaluation && (
              <Badge className="bg-blue-100 text-blue-800">
                #{evaluation.instance_id?.slice(0, 8)}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-gray-200 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading team data...</p>
              </div>
            </div>
          ) : isCompleted ? (
            /* View Details for Completed Evaluation */
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-800">Evaluation Completed</h3>
                  <p className="text-sm text-green-700">
                    This evaluation was completed on {evaluation.completed_at ? new Date(evaluation.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Evaluation Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Hospital:</span>
                    <p className="font-medium text-gray-900">{evaluation.hospital_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Triggered By:</span>
                    <p className="font-medium text-gray-900">{evaluation.triggered_by_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Assigned On:</span>
                    <p className="font-medium text-gray-900">
                      {evaluation.created_at ? new Date(evaluation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <p className="font-medium text-green-600 capitalize">{evaluation.status}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Evaluation Form for Pending/In-Progress */
            <div className="space-y-6">
              {/* Subchapter 1: Staff Feedback Table */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  Subchapter 1: Staff Feedback Table
                </h2>
                <p className="text-gray-600 text-sm mb-4">
                  Rate each staff member across the listed categories based on their performance and contribution.
                </p>

                {teamMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No team members found. You need to have staff members assigned to you to complete this evaluation.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                            Staff
                          </th>
                          <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                            <div>Compliance</div>
                            <div className="text-xs font-normal text-gray-600 mt-1">
                              (Adherence to policies, SOPs, NABH)
                            </div>
                          </th>
                          <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                            <div>Skill Gap</div>
                            <div className="text-xs font-normal text-gray-600 mt-1">
                              (Gaps in skills or training)
                            </div>
                          </th>
                          <th className="text-left p-3 font-bold text-gray-900">
                            <div>Patient Care</div>
                            <div className="text-xs font-normal text-gray-600 mt-1">
                              (Contribution to patient outcomes)
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map((member, index) => (
                          <tr key={member.employee_id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="p-3 font-medium text-gray-900 border-r border-gray-200">
                              <div>
                                <div className="font-semibold">{member.name}</div>
                                <div className="text-xs text-gray-600">{member.job_function || member.department}</div>
                              </div>
                            </td>
                            <td className="p-3 border-r border-gray-200">
                              <Select
                                value={staffFeedback[member.employee_id]?.compliance}
                                onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'compliance', value)}
                              >
                                <SelectTrigger className="w-full bg-white">
                                  <SelectValue placeholder="Choose" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4">Excellent (4)</SelectItem>
                                  <SelectItem value="3">Good (3)</SelectItem>
                                  <SelectItem value="2">Needs Improvement (2)</SelectItem>
                                  <SelectItem value="1">Poor (1)</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 border-r border-gray-200">
                              <Select
                                value={staffFeedback[member.employee_id]?.skill_gap}
                                onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'skill_gap', value)}
                              >
                                <SelectTrigger className="w-full bg-white">
                                  <SelectValue placeholder="Choose" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4">None (4)</SelectItem>
                                  <SelectItem value="3">Minor (3)</SelectItem>
                                  <SelectItem value="2">Moderate (2)</SelectItem>
                                  <SelectItem value="1">Significant (1)</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3">
                              <Select
                                value={staffFeedback[member.employee_id]?.patient_care}
                                onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'patient_care', value)}
                              >
                                <SelectTrigger className="w-full bg-white">
                                  <SelectValue placeholder="Choose" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4">Strongly Positive (4)</SelectItem>
                                  <SelectItem value="3">Positive (3)</SelectItem>
                                  <SelectItem value="2">Neutral (2)</SelectItem>
                                  <SelectItem value="1">Needs Improvement (1)</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Subchapter 2: Department-Level Questionnaire */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  Subchapter 2: Department-Level Questionnaire
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Evaluate the overall performance of your department across compliance, patient care, teamwork, and efficiency.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Compliance & Readiness */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Compliance & Readiness</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Compared to last quarter, how has your department strengthened compliance?
                    </p>
                    <Select
                      value={departmentFeedback.compliance_readiness}
                      onValueChange={(value) => handleDepartmentFeedbackChange('compliance_readiness', value)}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">Excellent improvement (4)</SelectItem>
                        <SelectItem value="3">Good improvement (3)</SelectItem>
                        <SelectItem value="2">Needs Improvement (2)</SelectItem>
                        <SelectItem value="1">Poor (1)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Patient Care & Safety */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Patient Care & Safety</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      How well is your department ensuring safe, patient-centered care?
                    </p>
                    <Select
                      value={departmentFeedback.patient_care_safety}
                      onValueChange={(value) => handleDepartmentFeedbackChange('patient_care_safety', value)}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">Very Strong (4)</SelectItem>
                        <SelectItem value="3">Strong (3)</SelectItem>
                        <SelectItem value="2">Moderate (2)</SelectItem>
                        <SelectItem value="1">Needs Improvement (1)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cost-Effectiveness */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Cost-Effectiveness</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Has training improved staff time and cost efficiency?
                    </p>
                    <Select
                      value={departmentFeedback.cost_effectiveness}
                      onValueChange={(value) => handleDepartmentFeedbackChange('cost_effectiveness', value)}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">Very effective (4)</SelectItem>
                        <SelectItem value="3">Effective (3)</SelectItem>
                        <SelectItem value="2">Slightly effective (2)</SelectItem>
                        <SelectItem value="1">Not effective (1)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Subchapter 3: Confirmation */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  Subchapter 3: Confirmation
                </h2>
                
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="modal-confirmation"
                    checked={confirmed}
                    onCheckedChange={setConfirmed}
                  />
                  <label
                    htmlFor="modal-confirmation"
                    className="text-gray-700 cursor-pointer select-none text-sm"
                  >
                    I confirm this feedback is accurate to the best of my knowledge.
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {isCompleted ? 'Close' : 'Cancel'}
          </Button>
          {!isCompleted && !loading && teamMembers.length > 0 && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90"
            >
              {submitting ? 'Submitting...' : 'Submit Evaluation'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaluationFormModal;
